import React, { useState } from "react";
import { Mail, Lock, ShieldCheck, Loader2 } from "lucide-react";
import API from "../api/axios";
import { useNavigate } from "react-router-dom";

export default function ForgetPassword() {
  const [step, setStep] = useState(1); // 1 = email, 2 = otp + new password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [form, setForm] = useState({ password: "", confirmPassword: "" });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  // STEP 1 → SEND OTP
  const sendOtp = async () => {
    setLoading(true);
    setError("");

    try {
      await API.post("/auth/forgot-password", { email });
      setStep(2);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to send OTP. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // STEP 2 → RESET PASSWORD (Verify OTP + Update password)
  const resetPassword = async () => {
    if (!otp) return setError("OTP is required");

    if (form.password !== form.confirmPassword)
      return setError("Passwords do not match!");

    setLoading(true);
    setError("");

    try {
      await API.post("/auth/reset-password", {
        email,
        otp,
        newPassword: form.password,
      });

      navigate("/login");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white p-8 rounded-3xl shadow-2xl border border-gray-200">
        <h1 className="text-3xl font-bold text-gray-800 text-center mb-6">
          Forgot Password
        </h1>

        {error && (
          <div className="bg-red-50 p-4 border border-red-300 rounded-xl mb-4 text-red-600 font-medium">
            {error}
          </div>
        )}

        {/* STEP 1 → EMAIL */}
        {step === 1 && (
          <>
            <p className="text-gray-600 mb-4 text-center">
              Enter your registered email to receive OTP.
            </p>

            <div className="relative mb-6">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="email"
                className="w-full pl-14 pr-4 py-4 border bg-gray-50 border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-600"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <button
              onClick={sendOtp}
              className="w-full bg-gray-800 text-white py-4 rounded-2xl font-bold hover:bg-gray-900 transition shadow-lg"
            >
              {loading ? <Loader2 className="animate-spin w-6 h-6 mx-auto" /> : "Send OTP"}
            </button>
          </>
        )}

        {/* STEP 2 → OTP + RESET PASSWORD */}
        {step === 2 && (
          <>
            <p className="text-gray-600 mb-4 text-center">
              Enter OTP and your new password.
            </p>

            {/* OTP */}
            <div className="relative mb-6">
              <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                maxLength={6}
                className="w-full pl-14 pr-4 py-4 border bg-gray-50 border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-600"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
            </div>

            {/* PASSWORD */}
            <div className="relative mb-4">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="password"
                className="w-full pl-14 pr-4 py-4 border bg-gray-50 border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-600"
                placeholder="New Password"
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
              />
            </div>

            {/* CONFIRM PASSWORD */}
            <div className="relative mb-6">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="password"
                className="w-full pl-14 pr-4 py-4 border bg-gray-50 border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-600"
                placeholder="Confirm Password"
                value={form.confirmPassword}
                onChange={(e) =>
                  setForm({ ...form, confirmPassword: e.target.value })
                }
              />
            </div>

            <button
              onClick={resetPassword}
              className="w-full bg-gray-800 text-white py-4 rounded-2xl font-bold hover:bg-gray-900 transition shadow-lg"
            >
              {loading ? <Loader2 className="animate-spin w-6 h-6 mx-auto" /> : "Reset Password"}
            </button>
          </>
        )}

        <p className="text-center mt-6 text-gray-600">
          <span
            onClick={() => navigate("/login")}
            className="font-bold text-gray-800 hover:underline cursor-pointer"
          >
            Back to Login
          </span>
        </p>
      </div>
    </div>
  );
}
